import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Seo } from "../components/Seo";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export function formatApiErrorDetail(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const GoogleIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
  </svg>
);

const inputClass = "mt-1";

export default function AuthPage() {
  const { user, login, loginWithPassword, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next") || "/";
  const refParam = searchParams.get("ref") || "";
  const [signin, setSignin] = useState({ email: "", password: "" });
  const [signup, setSignup] = useState({ name: "", email: "", password: "", referral: refParam });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (refParam) sessionStorage.setItem("eb_ref", refParam);
  }, [refParam]);

  useEffect(() => {
    if (user) navigate(next, { replace: true });
  }, [user, navigate, next]);

  const submitSignin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await loginWithPassword(signin.email, signin.password);
      toast.success("Welcome back");
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Sign in failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const submitSignup = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await register(signup.name, signup.email, signup.password, signup.referral.trim());
      sessionStorage.removeItem("eb_ref");
      toast.success("Account created — welcome!");
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Sign up failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative z-10 max-w-md mx-auto px-6 py-16 sm:py-24">
      <Seo title="Sign In" path="/login" noindex />
      <div className="text-center mb-8">
        <img src="/eb-logo.png" alt="Eternal Biolabs" className="h-12 w-auto mx-auto mb-4" />
        <h1 className="font-display text-xl sm:text-2xl uppercase tracking-wide text-[#1A1A1A]">Your Account</h1>
        <p className="text-sm text-[#6E675F] mt-2">Sign in to check out, track orders and earn reward points.</p>
      </div>

      <div className="border border-[#DDD7CF] rounded-lg bg-white p-8" data-testid="auth-card">
        <Tabs defaultValue="signin" onValueChange={() => setError("")}>
          <TabsList className="w-full mb-6 h-12 md:h-9">
            <TabsTrigger value="signin" className="flex-1 h-full" data-testid="auth-tab-signin">Sign In</TabsTrigger>
            <TabsTrigger value="signup" className="flex-1 h-full" data-testid="auth-tab-signup">Create Account</TabsTrigger>
          </TabsList>

          <TabsContent value="signin">
            <form onSubmit={submitSignin} className="space-y-4">
              <div>
                <Label htmlFor="si-email">Email</Label>
                <Input id="si-email" type="email" autoComplete="email" required data-testid="signin-email-input"
                  value={signin.email} onChange={(e) => setSignin((f) => ({ ...f, email: e.target.value }))} className={inputClass} />
              </div>
              <div>
                <Label htmlFor="si-password">Password</Label>
                <Input id="si-password" type="password" autoComplete="current-password" required data-testid="signin-password-input"
                  value={signin.password} onChange={(e) => setSignin((f) => ({ ...f, password: e.target.value }))} className={inputClass} />
              </div>
              <button type="submit" disabled={busy} data-testid="signin-submit-button"
                className="w-full bg-[#8C7B6B] text-white text-xs tracking-[0.15em] uppercase font-semibold px-8 py-3.5 rounded-none hover:bg-[#75665A] transition-colors disabled:opacity-60">
                {busy ? "Signing In…" : "Sign In"}
              </button>
            </form>
          </TabsContent>

          <TabsContent value="signup">
            <form onSubmit={submitSignup} className="space-y-4">
              <div>
                <Label htmlFor="su-name">Name</Label>
                <Input id="su-name" autoComplete="name" required data-testid="signup-name-input"
                  value={signup.name} onChange={(e) => setSignup((f) => ({ ...f, name: e.target.value }))} className={inputClass} />
              </div>
              <div>
                <Label htmlFor="su-email">Email</Label>
                <Input id="su-email" type="email" autoComplete="email" required data-testid="signup-email-input"
                  value={signup.email} onChange={(e) => setSignup((f) => ({ ...f, email: e.target.value }))} className={inputClass} />
              </div>
              <div>
                <Label htmlFor="su-password">Password (min. 8 characters)</Label>
                <Input id="su-password" type="password" autoComplete="new-password" required minLength={8} data-testid="signup-password-input"
                  value={signup.password} onChange={(e) => setSignup((f) => ({ ...f, password: e.target.value }))} className={inputClass} />
              </div>
              <div>
                <Label htmlFor="su-referral">Referred by (optional)</Label>
                <Input id="su-referral" type="email" placeholder="friend@email.com" data-testid="signup-referral-input"
                  value={signup.referral} onChange={(e) => setSignup((f) => ({ ...f, referral: e.target.value }))} className={inputClass} />
                <p className="text-[11px] text-[#6E675F] mt-1">
                  Enter your friend's account email — you both get 100 reward points after your first paid order.
                </p>
              </div>
              <button type="submit" disabled={busy} data-testid="signup-submit-button"
                className="w-full bg-[#8C7B6B] text-white text-xs tracking-[0.15em] uppercase font-semibold px-8 py-3.5 rounded-none hover:bg-[#75665A] transition-colors disabled:opacity-60">
                {busy ? "Creating Account…" : "Create Account"}
              </button>
            </form>
          </TabsContent>
        </Tabs>

        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mt-4" data-testid="auth-error">
            {error}
          </p>
        )}

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-[#DDD7CF]" />
          <span className="text-xs tracking-[0.2em] uppercase text-[#6E675F]">or</span>
          <div className="flex-1 h-px bg-[#DDD7CF]" />
        </div>

        <button type="button" onClick={login} data-testid="google-signin-button"
          className="w-full inline-flex items-center justify-center gap-2.5 border border-[#DDD7CF] text-[#1A1A1A] text-xs tracking-[0.15em] uppercase font-semibold px-8 py-3.5 rounded-none hover:bg-[#EFEBE6] transition-colors">
          <GoogleIcon /> Continue with Google
        </button>
        <p className="text-xs text-[#6E675F] mt-4 text-center">
          Same email = same account. Points and orders are shared either way you sign in.
        </p>
      </div>
    </div>
  );
}
